import {
  CarePlanContentInput,
  ClinicRequestContent,
  HomeCarePlanContent,
  OutputContentList,
  OutreachContent,
  RXQuestionContent,
  RxFlowConsent as RxFlowConsentModel,
  SOAPNoteContent,
  TriageSummaryContent,
} from "@/api/generated/models";
import { AssistantClientEvent } from "@/api/model/Conversation";
import PreClinicGate from "@/conversation/AssistantMessage/ClinicRequest/PreClinicGate";
import { captureException } from "@/sentry";
import type { ConsentKind } from "@/store/medicationHistory";
import { AssistantMessageTypes } from "@/types";
import CarePlan from "../CuraiMessage/CarePlan";
import CommentableContent from "./CommentableContent";
import DocumentPreview from "./DocumentPreview";
import HomeCarePlan from "./HomeCarePlan";
import MedicationHistoryGate from "./MedicationHistoryGate";
import OutputContent from "./OutputContent";
import OutputText from "./OutputText";
import OutreachMessage from "./OutreachMessage";
import RxFlowConsent from "./RxFlowConsent";
import SOAPNote from "./SOAPNote";
import TriageSummary from "./TriageSummary";
import ZocdocBooking from "./ZocdocBooking";

const AssistantMessage = ({
  type,
  content,
  ...props
}: AssistantClientEvent) => {
  switch (type) {
    case "refusal":
    case AssistantMessageTypes.OUTPUT_TEXT:
    case AssistantMessageTypes.SUBJECTIVE:
    case AssistantMessageTypes.SMS_GATED_CONTENT:
      return <OutputText {...props} type={type} content={content as string} />;
    case AssistantMessageTypes.RX_QUESTION:
    case AssistantMessageTypes.LOCATION_QUESTION:
      // Answered survey questions render as plain assistant bubbles in the transcript; the active one
      // is hidden upstream and shown in the rx panel instead.
      return (
        <OutputText
          {...props}
          type={AssistantMessageTypes.OUTPUT_TEXT}
          content={(content as RXQuestionContent)?.next_question ?? ""}
        />
      );
    case AssistantMessageTypes.OUTPUT_CONTENT:
      return (
        <OutputContent
          {...props}
          type={type}
          content={content as OutputContentList}
        />
      );
    case AssistantMessageTypes.TRIAGE_SUMMARY:
      return (
        <TriageSummary
          {...props}
          type={type}
          content={content as TriageSummaryContent}
        />
      );
    case AssistantMessageTypes.SOAP_NOTE:
      return (
        <SOAPNote {...props} type={type} content={content as SOAPNoteContent} />
      );
    case AssistantMessageTypes.CARE_PLAN:
      return (
        <CarePlan
          {...props}
          type={type}
          title="You visited Curai Health."
          content={content as CarePlanContentInput}
        />
      );
    case AssistantMessageTypes.HOME_CARE_PLAN:
      return (
        <HomeCarePlan
          {...props}
          type={type}
          content={content as HomeCarePlanContent}
        />
      );
    case AssistantMessageTypes.CLINIC_REQUEST:
      return (
        <PreClinicGate
          {...props}
          type={type}
          content={content as ClinicRequestContent}
        />
      );
    case AssistantMessageTypes.MEDICATION_HISTORY_REQUEST:
      return (
        <MedicationHistoryGate
          {...props}
          content={content as { consent_type?: ConsentKind }}
        />
      );
    case AssistantMessageTypes.CURAI_REDIRECT:
      // Historical redirects were backfilled to a CLINIC_REQUEST(type=VISIT) event,
      // which renders the CuraiRedirect UI. Suppress the legacy event to avoid a
      // duplicate card.
      return null;
    case AssistantMessageTypes.OUTREACH:
      return (
        <OutreachMessage
          {...props}
          type={type}
          content={content as OutreachContent}
        />
      );
    case AssistantMessageTypes.ASK_USER_QUESTIONS:
    case AssistantMessageTypes.PRE_CLINIC_STEPS:
    case AssistantMessageTypes.PRE_ASYNC_REQUIREMENTS:
      // Render nothing in the message stream — each is shown by a sticky panel, driven by the
      // session NoriConversation starts.
      return null;
    case AssistantMessageTypes.DOCUMENT_PREVIEW:
      return (
        <DocumentPreview
          {...props}
          type={type}
          content={content as { pdf_url: string; preview_image_urls: string[] }}
        />
      );
    case AssistantMessageTypes.COMMENTABLE_CONTENT:
      return (
        <CommentableContent
          {...props}
          type={type}
          content={
            content as {
              title: string;
              fields?: { label: string; value: string }[];
              body: string[];
            }
          }
        />
      );
    case AssistantMessageTypes.RX_FLOW_CONSENT:
      // Legacy rx flows only: the generalist flow hides this event (see isHiddenMessage) and drives
      // consent from the inline panel, so this renders the stream Continue button just as before.
      return (
        <RxFlowConsent
          {...props}
          type={type}
          content={content as RxFlowConsentModel}
        />
      );
    case AssistantMessageTypes.ZOCDOC_BOOKING:
      return <ZocdocBooking {...props} type={type} content={content as any} />;
    default:
      captureException(
        "Unexpected content type found while rendering assistant message.",
        {
          extra: { type, content },
        },
      );
      return null;
  }
};

export default AssistantMessage;
